import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Analytics as AnalyticsIcon,
  Balance as BalanceIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import client from "../../../../API";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";

const AdminCompanyTeams = ({ companyId }) => {
  const queryClient = useQueryClient();
  const { dialogState, openDialog } = useAlertDialog();
  
  // State for dialogs
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [creditUsageDialogOpen, setCreditUsageDialogOpen] = useState(false);
  const [userAssignDialogOpen, setUserAssignDialogOpen] = useState(false);
  
  // State for forms
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    name: "",
    description: "",
    team_leader_id: "",
    can_manage_members: false
  });
  const [creditFormData, setCreditFormData] = useState({
    allocated_credit_line: "",
    reason: ""
  });
  const [creditUsageData, setCreditUsageData] = useState({
    amount: "",
    reason: "",
    operation: "use"
  });
  const [rebalanceData, setRebalanceData] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");

  // Fetch data
  const {
    data: teamStructure,
    isLoading: teamsLoading,
    refetch: refetchTeams,
  } = useQuery(
    ["adminTeamStructure", companyId],
    () => client.companies.teams.list(companyId),
    { enabled: !!companyId }
  );

  const {
    data: creditSummary,
    isLoading: creditLoading,
    refetch: refetchCredit,
  } = useQuery(
    ["adminCreditSummary", companyId],
    () => client.companies.teams.getCreditSummary(companyId),
    { enabled: !!companyId }
  );

  const {
    data: companyUsers,
    isLoading: usersLoading,
  } = useQuery(
    ["companyUsers", companyId],
    () => client.companies.getUsers(companyId),
    { enabled: !!companyId }
  );

  const {
    data: creditLedger,
    isLoading: ledgerLoading,
  } = useQuery(
    ["teamCreditLedger", companyId],
    () => client.companies.teams.getCreditLedger(companyId),
    { enabled: !!companyId }
  );

  // Mutations
  const createTeamMutation = useMutation(
    (data) => client.companies.teams.create(companyId, data),
    {
      onSuccess: () => {
        refetchTeams();
        refetchCredit();
        setTeamDialogOpen(false);
        resetTeamForm();
        openDialog("Success", "Team created successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to create team");
      },
    }
  );

  const updateTeamMutation = useMutation(
    ({ id, data }) => client.companies.teams.update(companyId, id, data),
    {
      onSuccess: () => {
        refetchTeams();
        setTeamDialogOpen(false);
        resetTeamForm();
        openDialog("Success", "Team updated successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to update team");
      },
    }
  );

  const deleteTeamMutation = useMutation(
    (teamId) => client.companies.teams.delete(companyId, teamId),
    {
      onSuccess: () => {
        refetchTeams();
        refetchCredit();
        openDialog("Success", "Team deleted successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to delete team");
      },
    }
  );

  const allocateCreditMutation = useMutation(
    ({ teamId, data }) => client.companies.teams.allocateCredit(companyId, teamId, data),
    {
      onSuccess: () => {
        refetchTeams();
        refetchCredit();
        setCreditDialogOpen(false);
        setCreditFormData({ allocated_credit_line: "", reason: "" });
        openDialog("Success", "Credit allocated successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to allocate credit");
      },
    }
  );

  const creditUsageMutation = useMutation(
    ({ teamId, operation, data }) => {
      if (operation === "use") {
        return client.companies.teams.useCredit(companyId, teamId, data);
      } else {
        return client.companies.teams.releaseCredit(companyId, teamId, data);
      }
    },
    {
      onSuccess: () => {
        refetchTeams();
        refetchCredit();
        setCreditUsageDialogOpen(false);
        setCreditUsageData({ amount: "", reason: "", operation: "use" });
        openDialog("Success", "Credit usage updated successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to update credit usage");
      },
    }
  );

  const rebalanceCreditMutation = useMutation(
    (data) => client.companies.teams.rebalance(companyId, data),
    {
      onSuccess: () => {
        refetchTeams();
        refetchCredit();
        setRebalanceDialogOpen(false);
        openDialog("Success", "Credit rebalanced successfully!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to rebalance credit");
      },
    }
  );

  const assignUserMutation = useMutation(
    ({ teamId, userId }) => client.companies.teams.assignUser(companyId, teamId, userId),
    {
      onSuccess: () => {
        refetchTeams();
        setUserAssignDialogOpen(false);
        setSelectedUserId("");
        openDialog("Success", "User assigned to team!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to assign user");
      },
    }
  );

  const removeUserMutation = useMutation(
    ({ teamId, userId }) => client.companies.teams.removeUser(companyId, teamId, userId),
    {
      onSuccess: () => {
        refetchTeams();
        openDialog("Success", "User removed from team!");
      },
      onError: (error) => {
        openDialog("Error", error?.response?.data?.error || "Failed to remove user");
      },
    }
  );

  // Helper functions
  const resetTeamForm = () => {
    setTeamFormData({
      name: "",
      description: "",
      team_leader_id: "",
      can_manage_members: false
    });
    setEditingTeam(null);
  };

  const handleCreateTeam = () => {
    setEditingTeam(null);
    resetTeamForm();
    setTeamDialogOpen(true);
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamFormData({
      name: team.name,
      description: team.description || "",
      team_leader_id: team.team_leader_id || "",
      can_manage_members: team.can_manage_members || false
    });
    setTeamDialogOpen(true);
  };

  const handleTeamSubmit = () => {
    if (editingTeam) {
      updateTeamMutation.mutate({ id: editingTeam.id, data: teamFormData });
    } else {
      createTeamMutation.mutate(teamFormData);
    }
  };

  const handleCreditManagement = (team, operation = "allocate") => {
    setSelectedTeam(team);
    if (operation === "allocate") {
      setCreditFormData({
        allocated_credit_line: team.allocated_credit_line || "",
        reason: ""
      });
      setCreditDialogOpen(true);
    } else {
      setCreditUsageData({
        amount: "",
        reason: "",
        operation
      });
      setCreditUsageDialogOpen(true);
    }
  };

  const handleRebalanceCredit = () => {
    if (creditSummary?.teams) {
      const initialData = creditSummary.teams.map(team => ({
        team_id: team.id,
        team_name: team.name,
        current_allocation: parseFloat(team.allocated_credit_line),
        allocation: parseFloat(team.allocated_credit_line),
        used_credit: parseFloat(team.used_credit)
      }));
      setRebalanceData(initialData);
      setRebalanceDialogOpen(true);
    }
  };

  const handleUserAssignment = (team) => {
    setSelectedTeam(team);
    setUserAssignDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  const getCreditStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'frozen': return 'error';
      default: return 'default';
    }
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'info';
    return 'success';
  };

  // Computed data
  const teams = teamStructure?.teams || [];
  const unassignedMembers = teamStructure?.unassigned_members || [];
  const allMembers = teamStructure?.all_members || [];
  const totalAllocationAmount = rebalanceData.reduce((sum, item) => sum + (item.allocation || 0), 0);

  if (teamsLoading || creditLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!companyId) {
    return (
      <Alert severity="warning">
        Please save the company first to manage teams.
      </Alert>
    );
  }

  return (
    <Box>
      <AlertDialog alertState={dialogState} />
      
      {/* Header with action buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Team Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTeam}
            sx={{ textTransform: "none" }}
          >
            Create Team
          </Button>
          {creditSummary && (
            <Button
              variant="outlined"
              startIcon={<BalanceIcon />}
              onClick={handleRebalanceCredit}
              sx={{ textTransform: "none" }}
            >
              Rebalance Credit
            </Button>
          )}
        </Box>
      </Box>

      {/* Credit Summary */}
      {creditSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: "primary.50" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
                  {formatCurrency(creditSummary.credit_summary.total_credit_line)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Credit Line
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: "success.50" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "success.main" }}>
                  {formatCurrency(creditSummary.credit_summary.total_allocated)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Allocated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: "warning.50" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "warning.main" }}>
                  {formatCurrency(creditSummary.credit_summary.total_used)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Used
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: "info.50" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "info.main" }}>
                  {formatCurrency(creditSummary.credit_summary.available_for_allocation)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Teams List */}
      <Box sx={{ mb: 3 }}>
        {teams.length > 0 ? (
          teams.map((team) => {
            const utilizationPercent = team.credit_utilization_percentage || 0;
            return (
              <Accordion key={team.id} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                    <GroupIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {team.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {team.description || "No description"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      <Chip 
                        label={`${team.member_count} members`} 
                        size="small" 
                        color="primary"
                      />
                      <Chip 
                        label={formatCurrency(team.allocated_credit_line)}
                        size="small" 
                        color="info"
                      />
                      <Chip 
                        label={team.credit_status || 'active'}
                        size="small" 
                        color={getCreditStatusColor(team.credit_status)}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    {/* Team Info */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Team Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Team Leader: {team.team_leader ? 
                            `${team.team_leader.first_name} ${team.team_leader.last_name}` : 
                            "None assigned"
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Can Manage Members: {team.can_manage_members ? "Yes" : "No"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditTeam(team)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonAddIcon />}
                          onClick={() => handleUserAssignment(team)}
                        >
                          Assign User
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            if (confirm(`Delete team "${team.name}"?`)) {
                              deleteTeamMutation.mutate(team.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Grid>

                    {/* Credit Info */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Credit Management
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2">Allocated:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(team.allocated_credit_line)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2">Used:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(team.used_credit)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                          <Typography variant="body2">Available:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(team.available_credit)}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Utilization: {utilizationPercent.toFixed(1)}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(utilizationPercent, 100)}
                            color={getUtilizationColor(utilizationPercent)}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AttachMoneyIcon />}
                          onClick={() => handleCreditManagement(team, "allocate")}
                        >
                          Allocate
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<TrendingUpIcon />}
                          onClick={() => handleCreditManagement(team, "use")}
                        >
                          Use
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AccountBalanceIcon />}
                          onClick={() => handleCreditManagement(team, "release")}
                        >
                          Release
                        </Button>
                      </Box>
                    </Grid>

                    {/* Team Members */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Team Members ({team.member_count})
                      </Typography>
                      {team.members && team.members.length > 0 ? (
                        <Grid container spacing={2}>
                          {team.members.map((member) => (
                            <Grid item xs={12} sm={6} md={4} key={member.id}>
                              <Card variant="outlined" sx={{ position: "relative" }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar src={member.phphref} sx={{ width: 32, height: 32 }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle2">
                                        {member.first_name} {member.last_name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {member.phone_number || "No phone"}
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeUserMutation.mutate({ 
                                        teamId: team.id, 
                                        userId: member.id 
                                      })}
                                    >
                                      <PersonRemoveIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No members assigned to this team.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Card>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <GroupIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No teams created yet
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateTeam}>
                Create First Team
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Unassigned Members */}
      {unassignedMembers.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Unassigned Members" />
          <CardContent>
            <Grid container spacing={2}>
              {unassignedMembers.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member.id}>
                  <Card variant="outlined" sx={{ borderStyle: "dashed" }}>
                    <CardContent sx={{ textAlign: "center", p: 2 }}>
                      <Avatar
                        src={member.phphref}
                        sx={{ width: 40, height: 40, mb: 1, margin: "0 auto" }}
                      />
                      <Typography variant="subtitle2">
                        {member.first_name} {member.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.phone_number || "No phone"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Team Create/Edit Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTeam ? "Edit Team" : "Create New Team"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={teamFormData.name}
                onChange={(e) => setTeamFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={teamFormData.description}
                onChange={(e) => setTeamFormData(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Team Leader</InputLabel>
                <Select
                  value={teamFormData.team_leader_id}
                  onChange={(e) => setTeamFormData(prev => ({ ...prev, team_leader_id: e.target.value }))}
                  label="Team Leader"
                >
                  <MenuItem value="">No Leader</MenuItem>
                  {allMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={teamFormData.can_manage_members}
                    onChange={(e) => setTeamFormData(prev => ({ 
                      ...prev, 
                      can_manage_members: e.target.checked 
                    }))}
                  />
                }
                label="Team leader can manage members"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTeamSubmit}
            disabled={createTeamMutation.isLoading || updateTeamMutation.isLoading}
          >
            {createTeamMutation.isLoading || updateTeamMutation.isLoading ? 
              "Saving..." : (editingTeam ? "Update Team" : "Create Team")
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Allocation Dialog */}
      <Dialog open={creditDialogOpen} onClose={() => setCreditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Allocate Credit to {selectedTeam?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allocated Credit Line"
                type="number"
                value={creditFormData.allocated_credit_line}
                onChange={(e) => setCreditFormData(prev => ({
                  ...prev,
                  allocated_credit_line: e.target.value
                }))}
                helperText={
                  selectedTeam && 
                  `Current: ${formatCurrency(selectedTeam.allocated_credit_line)} | ` +
                  `Used: ${formatCurrency(selectedTeam.used_credit)} | ` +
                  `Available Company Credit: ${formatCurrency(creditSummary?.credit_summary?.available_for_allocation || 0)}`
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Allocation"
                multiline
                rows={3}
                value={creditFormData.reason}
                onChange={(e) => setCreditFormData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => allocateCreditMutation.mutate({
              teamId: selectedTeam.id,
              data: creditFormData
            })}
            disabled={allocateCreditMutation.isLoading}
          >
            {allocateCreditMutation.isLoading ? "Allocating..." : "Allocate Credit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Usage Dialog */}
      <Dialog open={creditUsageDialogOpen} onClose={() => setCreditUsageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {creditUsageData.operation === "use" ? "Use Team Credit" : "Release Team Credit"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={creditUsageData.amount}
                onChange={(e) => setCreditUsageData(prev => ({
                  ...prev,
                  amount: e.target.value
                }))}
                helperText={
                  selectedTeam && (
                    creditUsageData.operation === "use" 
                      ? `Available: ${formatCurrency(selectedTeam.available_credit)}`
                      : `Currently Used: ${formatCurrency(selectedTeam.used_credit)}`
                  )
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={creditUsageData.reason}
                onChange={(e) => setCreditUsageData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditUsageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => creditUsageMutation.mutate({
              teamId: selectedTeam.id,
              operation: creditUsageData.operation,
              data: {
                amount: creditUsageData.amount,
                reason: creditUsageData.reason
              }
            })}
            disabled={creditUsageMutation.isLoading}
          >
            {creditUsageMutation.isLoading ? "Processing..." : 
             (creditUsageData.operation === "use" ? "Use Credit" : "Release Credit")
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={userAssignDialogOpen} onClose={() => setUserAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign User to {selectedTeam?.name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Select User"
            >
              {(unassignedMembers || companyUsers || []).map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.first_name} {member.last_name} - {member.status ? "Active" : "Pending"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => assignUserMutation.mutate({
              teamId: selectedTeam.id,
              userId: parseInt(selectedUserId)
            })}
            disabled={assignUserMutation.isLoading || !selectedUserId}
          >
            {assignUserMutation.isLoading ? "Assigning..." : "Assign User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Rebalancing Dialog */}
      <Dialog open={rebalanceDialogOpen} onClose={() => setRebalanceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Rebalance Team Credit Allocations</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Adjust credit allocations across teams. Total allocation cannot exceed company credit line.
            </Typography>
            {creditSummary && (
              <Typography variant="caption" color="text.secondary">
                Company Credit Line: {formatCurrency(creditSummary.credit_summary.total_credit_line)} | 
                New Total: {formatCurrency(totalAllocationAmount)} | 
                Remaining: {formatCurrency(parseFloat(creditSummary.credit_summary.total_credit_line) - totalAllocationAmount)}
              </Typography>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Team</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>Used</TableCell>
                  <TableCell>New Allocation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rebalanceData.map((item, index) => (
                  <TableRow key={item.team_id}>
                    <TableCell>{item.team_name}</TableCell>
                    <TableCell>{formatCurrency(item.current_allocation)}</TableCell>
                    <TableCell>{formatCurrency(item.used_credit)}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.allocation}
                        onChange={(e) => {
                          const newData = [...rebalanceData];
                          newData[index].allocation = parseFloat(e.target.value) || 0;
                          setRebalanceData(newData);
                        }}
                        error={item.allocation < item.used_credit}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebalanceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const team_allocations = rebalanceData.map(item => ({
                team_id: item.team_id,
                allocation: item.allocation
              }));
              
              rebalanceCreditMutation.mutate({
                team_allocations,
                reason: "Credit rebalancing operation"
              });
            }}
            disabled={
              rebalanceCreditMutation.isLoading ||
              (creditSummary && totalAllocationAmount > parseFloat(creditSummary.credit_summary.total_credit_line)) ||
              rebalanceData.some(item => item.allocation < item.used_credit)
            }
          >
            {rebalanceCreditMutation.isLoading ? "Rebalancing..." : "Apply Rebalancing"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCompanyTeams;